import { useState, useEffect, useCallback } from 'react';
import { useSubscription } from '../../../hooks/useSubscription';
import { usePlanLimits } from '../../../hooks/usePlanLimits';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────
interface DiagnosticData {
  subscription_type: string;
  billing_cycle: string;
  plan_name: string;
  plan_slug: string;
  sub_status: string;
  monthly_limit: number;
  accounts_limit: number;
  cards_limit: number;
  goals_limit: number;
  reports_enabled: boolean;
  started_at: string | null;
  expires_at: string | null;
  server_check_allowed: boolean | null;
  server_check_current: number | null;
  server_check_limit: number | null;
  server_check_message: string | null;
}

interface DiagnosticItemProps {
  label: string;
  value: React.ReactNode;
  icon: string;
  status?: 'ok' | 'warn' | 'error' | 'info';
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtLimit = (v: number) => (v === -1 ? 'Ilimitado' : String(v));
const fmtDate = (d: string | null) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const SUB_TYPE_LABELS: Record<string, { label: string; color: string; icon: string }> = {
  free:          { label: 'Gratuito',          color: 'text-gray-600 bg-gray-100',      icon: 'ri-user-line' },
  paid:          { label: 'Pago (recorrente)', color: 'text-emerald-700 bg-emerald-50', icon: 'ri-vip-crown-line' },
  lifetime:      { label: 'Vitalício',         color: 'text-amber-700 bg-amber-50',     icon: 'ri-vip-diamond-line' },
  vitalicio:     { label: 'Vitalício (BR)',    color: 'text-amber-700 bg-amber-50',     icon: 'ri-vip-diamond-line' },
  complimentary: { label: 'Cortesia',          color: 'text-violet-700 bg-violet-50',   icon: 'ri-gift-line' },
  manual:        { label: 'Manual (admin)',    color: 'text-violet-700 bg-violet-50',   icon: 'ri-admin-line' },
  gifted:        { label: 'Brinde',            color: 'text-pink-700 bg-pink-50',       icon: 'ri-gift-2-line' },
  courtesy:      { label: 'Cortesia',          color: 'text-pink-700 bg-pink-50',       icon: 'ri-heart-line' },
};

const UNLIMITED_TYPES = new Set(['lifetime', 'vitalicio', 'complimentary', 'manual', 'gifted', 'courtesy']);
const PAID_TYPES      = new Set(['paid', 'lifetime', 'vitalicio', 'complimentary', 'manual', 'gifted', 'courtesy']);

// ─── Sub-components ───────────────────────────────────────────────────────────
const DiagnosticItem = ({ label, value, icon, status = 'info' }: DiagnosticItemProps) => {
  const statusCls = {
    ok:    'text-emerald-600 bg-emerald-50',
    warn:  'text-amber-600 bg-amber-50',
    error: 'text-red-600 bg-red-50',
    info:  'text-gray-500 bg-gray-50',
  }[status];

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${statusCls}`}>
        <i className={`${icon} text-xs`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-400 font-500 mb-0.5">{label}</p>
        <div className="text-sm font-600 text-gray-800">{value}</div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
export const PlanDiagnosticCard = () => {
  const { user } = useAuth();
  const { subscription, isPaid } = useSubscription();
  const { plan, monthlyTransactionLimit } = usePlanLimits();

  const [diag, setDiag] = useState<DiagnosticData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [serverChecking, setServerChecking] = useState(false);
  const [serverResult, setServerResult] = useState<{
    allowed: boolean | null;
    current: number | null;
    limit: number | null;
    message: string | null;
    checked: boolean;
  }>({ allowed: null, current: null, limit: null, message: null, checked: false });

  const loadDiagnostic = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setLoadError(null);
    try {
      // First try: filter by active status
      const { data: subData, error: subError } = await supabase
        .from('subscriptions')
        .select(`
          subscription_type,
          billing_cycle,
          status,
          started_at,
          expires_at,
          plans (
            name, slug,
            monthly_transactions_limit,
            accounts_limit,
            cards_limit,
            goals_limit,
            reports_enabled
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (subError) {
        console.error('[PlanDiagnosticCard] query error (active):', subError);
        throw new Error(subError.message);
      }

      // Fallback: if no active subscription found, get the most recent one regardless of status
      let finalData = subData;
      if (!finalData) {
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('subscriptions')
          .select(`
            subscription_type,
            billing_cycle,
            status,
            started_at,
            expires_at,
            plans (
              name, slug,
              monthly_transactions_limit,
              accounts_limit,
              cards_limit,
              goals_limit,
              reports_enabled
            )
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (fallbackError) {
          console.error('[PlanDiagnosticCard] fallback query error:', fallbackError);
          throw new Error(fallbackError.message);
        }
        finalData = fallbackData;
      }

      if (finalData) {
        const p = finalData.plans as {
          name: string; slug: string;
          monthly_transactions_limit: number;
          accounts_limit: number;
          cards_limit: number;
          goals_limit: number;
          reports_enabled: boolean;
        } | null;

        setDiag({
          subscription_type:  finalData.subscription_type ?? 'free',
          billing_cycle:      finalData.billing_cycle ?? '—',
          sub_status:         finalData.status ?? '—',
          started_at:         finalData.started_at ?? null,
          expires_at:         finalData.expires_at ?? null,
          plan_name:          p?.name ?? subscription?.plans?.name ?? 'Gratuito',
          plan_slug:          p?.slug ?? subscription?.plans?.slug ?? 'free',
          monthly_limit:      p?.monthly_transactions_limit ?? 30,
          accounts_limit:     p?.accounts_limit ?? 1,
          cards_limit:        p?.cards_limit ?? 1,
          goals_limit:        p?.goals_limit ?? 2,
          reports_enabled:    p?.reports_enabled ?? false,
          server_check_allowed: null,
          server_check_current: null,
          server_check_limit:   null,
          server_check_message: null,
        });
      } else {
        // No subscription at all — build from hooks
        setDiag({
          subscription_type:  'free',
          billing_cycle:      '—',
          sub_status:         'active',
          started_at:         null,
          expires_at:         null,
          plan_name:          subscription?.plans?.name ?? plan?.name ?? 'Gratuito',
          plan_slug:          subscription?.plans?.slug ?? plan?.slug ?? 'free',
          monthly_limit:      plan?.monthly_transactions_limit ?? 30,
          accounts_limit:     plan?.accounts_limit ?? 1,
          cards_limit:        plan?.cards_limit ?? 1,
          goals_limit:        plan?.goals_limit ?? 2,
          reports_enabled:    plan?.reports_enabled ?? false,
          server_check_allowed: null,
          server_check_current: null,
          server_check_limit:   null,
          server_check_message: null,
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro desconhecido';
      console.error('[PlanDiagnosticCard] loadDiagnostic failed:', msg);
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  }, [user, subscription, plan]);

  const runServerCheck = async () => {
    setServerChecking(true);
    try {
      const { data, error } = await supabase.functions.invoke('validate-transaction-limit', {
        method: 'POST',
      });
      if (error) throw error;
      setServerResult({
        allowed:  data.allowed,
        current:  data.current ?? null,
        limit:    data.limit ?? null,
        message:  data.message ?? null,
        checked:  true,
      });
    } catch {
      setServerResult({
        allowed:  null,
        current:  null,
        limit:    null,
        message:  'Não foi possível verificar o servidor.',
        checked:  true,
      });
    } finally {
      setServerChecking(false);
    }
  };

  useEffect(() => {
    if (open && !diag && !loading) {
      loadDiagnostic();
    }
  }, [open, diag, loading, loadDiagnostic]);

  const subTypeMeta = SUB_TYPE_LABELS[diag?.subscription_type ?? 'free'] ?? SUB_TYPE_LABELS['free'];
  const isSystemPaid = PAID_TYPES.has(diag?.subscription_type ?? 'free');
  const isUnlimited  = UNLIMITED_TYPES.has(diag?.subscription_type ?? 'free') || diag?.monthly_limit === -1;

  // ── Detect mismatches ────────────────────────────────────────────────────
  const hasSubTypeMismatch = diag
    ? (PAID_TYPES.has(diag.subscription_type) !== isPaid)
    : false;

  const hookSaysUnlimited = monthlyTransactionLimit === null;
  const limitMismatch = diag
    ? (isUnlimited && !hookSaysUnlimited && !hasSubTypeMismatch)
    : false;

  const anyIssue = hasSubTypeMismatch || limitMismatch;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Header / toggle */}
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors cursor-pointer text-left"
      >
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${anyIssue ? 'bg-amber-50' : 'bg-emerald-50'}`}>
          <i className={`text-base ${anyIssue ? 'ri-alert-line text-amber-500' : 'ri-stethoscope-line text-emerald-500'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-700 text-gray-800">
            Diagnóstico do plano
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {anyIssue
              ? 'Divergência detectada — clique para ver detalhes'
              : 'Limites reais, uso atual e tipo de assinatura'}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {anyIssue && (
            <span className="text-xs font-700 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
              Atenção
            </span>
          )}
          <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line text-gray-400`} />
        </div>
      </button>

      {/* Body */}
      {open && (
        <div className="border-t border-gray-100 px-5 pb-5 pt-4 space-y-5">

          {loading && (
            <div className="space-y-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-10 bg-gray-100 rounded-xl animate-pulse" />
              ))}
            </div>
          )}

          {/* Error state */}
          {!loading && loadError && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center">
                <i className="ri-error-warning-line text-red-400 text-xl" />
              </div>
              <div>
                <p className="text-sm font-700 text-gray-800 mb-1">Não foi possível carregar</p>
                <p className="text-xs text-gray-400 mb-3 max-w-xs mx-auto">
                  Falha ao buscar os dados de assinatura. Verifique sua conexão e tente novamente.
                </p>
                <button
                  onClick={() => { setLoadError(null); setDiag(null); loadDiagnostic(); }}
                  className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors mx-auto"
                >
                  <i className="ri-refresh-line" />Tentar novamente
                </button>
              </div>
            </div>
          )}

          {!loading && !loadError && diag && (
            <>
              {/* ── Mismatch banner ─────────────────────────────────────── */}
              {anyIssue && (
                <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <i className="ri-alert-fill text-amber-500 text-base mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-700 text-amber-800 mb-0.5">Divergência detectada</p>
                    {hasSubTypeMismatch && (
                      <p className="text-xs text-amber-700 leading-relaxed">
                        O campo <strong>subscription_type</strong> ({diag.subscription_type}) não é reconhecido
                        como pago pelo sistema frontend. A conta pode estar sendo tratada como gratuita
                        indevidamente.
                      </p>
                    )}
                    {limitMismatch && (
                      <p className="text-xs text-amber-700 leading-relaxed mt-1">
                        O plano indica limite ilimitado, mas o hook retorna um valor numérico.
                        Pode ser instabilidade de cache — recarregue a página.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* ── Subscription identity ───────────────────────────────── */}
              <div>
                <p className="text-xs font-700 text-gray-400 uppercase tracking-wide mb-2">
                  Identidade da assinatura
                </p>
                <div className="bg-gray-50 rounded-xl px-4 divide-y divide-gray-100">
                  <DiagnosticItem
                    label="Plano ativo"
                    icon="ri-vip-crown-line"
                    status={isSystemPaid ? 'ok' : 'info'}
                    value={
                      <span className="flex items-center gap-2">
                        {diag.plan_name}
                        <span className="text-xs font-500 text-gray-400">({diag.plan_slug})</span>
                      </span>
                    }
                  />
                  <DiagnosticItem
                    label="subscription_type (banco)"
                    icon="ri-database-2-line"
                    status={PAID_TYPES.has(diag.subscription_type) ? 'ok' : 'info'}
                    value={
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-700 ${subTypeMeta.color}`}>
                        <i className={`${subTypeMeta.icon} text-xs`} />
                        {diag.subscription_type} — {subTypeMeta.label}
                      </span>
                    }
                  />
                  <DiagnosticItem
                    label="Reconhecido como premium (frontend)"
                    icon={isPaid ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'}
                    status={isPaid ? 'ok' : hasSubTypeMismatch ? 'error' : 'info'}
                    value={isPaid ? 'Sim — acesso completo' : 'Não — acesso limitado'}
                  />
                  <DiagnosticItem
                    label="Ciclo de cobrança"
                    icon="ri-calendar-line"
                    status="info"
                    value={diag.billing_cycle}
                  />
                  <DiagnosticItem
                    label="Status da assinatura"
                    icon="ri-shield-check-line"
                    status={diag.sub_status === 'active' ? 'ok' : 'warn'}
                    value={diag.sub_status}
                  />
                  <DiagnosticItem
                    label="Ativo desde"
                    icon="ri-calendar-event-line"
                    status="info"
                    value={fmtDate(diag.started_at)}
                  />
                  {diag.expires_at && (
                    <DiagnosticItem
                      label="Expira em"
                      icon="ri-time-line"
                      status="info"
                      value={fmtDate(diag.expires_at)}
                    />
                  )}
                </div>
              </div>

              {/* ── Plan limits ─────────────────────────────────────────── */}
              <div>
                <p className="text-xs font-700 text-gray-400 uppercase tracking-wide mb-2">
                  Limites reais do plano (valores no banco)
                </p>
                <div className="bg-gray-50 rounded-xl px-4 divide-y divide-gray-100">
                  {[
                    { label: 'Lançamentos/mês',    value: fmtLimit(diag.monthly_limit),  icon: 'ri-exchange-funds-line' },
                    { label: 'Contas bancárias',   value: fmtLimit(diag.accounts_limit), icon: 'ri-bank-line' },
                    { label: 'Cartões de crédito', value: fmtLimit(diag.cards_limit),    icon: 'ri-bank-card-line' },
                    { label: 'Metas financeiras',  value: fmtLimit(diag.goals_limit),    icon: 'ri-focus-3-line' },
                    { label: 'Relatórios',         value: diag.reports_enabled ? 'Habilitado' : 'Desabilitado', icon: 'ri-bar-chart-2-line' },
                  ].map(item => (
                    <DiagnosticItem
                      key={item.label}
                      label={item.label}
                      value={
                        <span className={item.value === 'Ilimitado' || item.value === 'Habilitado'
                          ? 'text-emerald-600'
                          : item.value === 'Desabilitado'
                          ? 'text-gray-400'
                          : 'text-gray-800'
                        }>
                          {item.value === 'Ilimitado' && (
                            <i className="ri-infinity-line mr-1 text-emerald-500" />
                          )}
                          {item.value}
                        </span>
                      }
                      icon={item.icon}
                      status={item.value === 'Ilimitado' || item.value === 'Habilitado' ? 'ok' : 'info'}
                    />
                  ))}
                </div>
              </div>

              {/* ── Hook readings ───────────────────────────────────────── */}
              <div>
                <p className="text-xs font-700 text-gray-400 uppercase tracking-wide mb-2">
                  Leitura atual do frontend (hooks)
                </p>
                <div className="bg-gray-50 rounded-xl px-4 divide-y divide-gray-100">
                  <DiagnosticItem
                    label="isPaid (useSubscription)"
                    icon={isPaid ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'}
                    status={isPaid ? 'ok' : 'info'}
                    value={String(isPaid)}
                  />
                  <DiagnosticItem
                    label="monthlyTransactionLimit (usePlanLimits)"
                    icon="ri-exchange-funds-line"
                    status={monthlyTransactionLimit === null ? 'ok' : 'info'}
                    value={
                      monthlyTransactionLimit === null
                        ? <span className="text-emerald-600"><i className="ri-infinity-line mr-1" />null (ilimitado)</span>
                        : String(monthlyTransactionLimit)
                    }
                  />
                </div>
              </div>

              {/* ── Server-side check ───────────────────────────────────── */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-700 text-gray-400 uppercase tracking-wide">
                    Validação no servidor (Edge Function)
                  </p>
                  {!serverResult.checked && (
                    <button
                      onClick={runServerCheck}
                      disabled={serverChecking}
                      className="flex items-center gap-1.5 px-3 py-1 text-xs font-600 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
                    >
                      {serverChecking
                        ? <><i className="ri-loader-4-line animate-spin" />Verificando...</>
                        : <><i className="ri-server-line" />Verificar agora</>
                      }
                    </button>
                  )}
                </div>

                {!serverResult.checked && !serverChecking && (
                  <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 rounded-xl">
                    <i className="ri-information-line text-gray-400 text-sm shrink-0" />
                    <p className="text-xs text-gray-500">
                      Clique em &quot;Verificar agora&quot; para consultar o servidor e confirmar se
                      seu plano está sendo validado corretamente pelo backend.
                    </p>
                  </div>
                )}

                {serverResult.checked && (
                  <div className={`px-4 py-3 rounded-xl border ${
                    serverResult.allowed === null
                      ? 'bg-gray-50 border-gray-200'
                      : serverResult.allowed
                      ? 'bg-emerald-50 border-emerald-200'
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-start gap-2">
                      <i className={`text-base mt-0.5 shrink-0 ${
                        serverResult.allowed === null ? 'ri-alert-line text-gray-400'
                        : serverResult.allowed ? 'ri-checkbox-circle-fill text-emerald-500'
                        : 'ri-close-circle-fill text-red-500'
                      }`} />
                      <div className="space-y-1">
                        <p className={`text-sm font-700 ${
                          serverResult.allowed === null ? 'text-gray-600'
                          : serverResult.allowed ? 'text-emerald-800'
                          : 'text-red-800'
                        }`}>
                          {serverResult.allowed === null
                            ? 'Não foi possível verificar'
                            : serverResult.allowed
                            ? 'Pode criar lançamentos'
                            : 'Limite atingido — bloqueado'}
                        </p>
                        {serverResult.message && (
                          <p className={`text-xs ${
                            serverResult.allowed === null ? 'text-gray-500'
                            : serverResult.allowed ? 'text-emerald-700'
                            : 'text-red-700'
                          }`}>
                            {serverResult.message}
                          </p>
                        )}
                        {serverResult.current !== null && serverResult.limit !== null && (
                          <p className="text-xs text-gray-500">
                            Contagem servidor: <strong>{serverResult.current}</strong> / {serverResult.limit}
                          </p>
                        )}
                        {serverResult.allowed !== null && serverResult.limit === null && serverResult.allowed && (
                          <p className="text-xs text-emerald-600 font-500">
                            <i className="ri-infinity-line mr-1" />Sem limite configurado para este plano
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => { setServerResult({ allowed: null, current: null, limit: null, message: null, checked: false }); }}
                      className="mt-2 text-xs text-gray-400 hover:text-gray-600 cursor-pointer underline"
                    >
                      Reverificar
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {!loading && !loadError && !diag && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center">
                <i className="ri-file-unknow-line text-gray-300 text-xl" />
              </div>
              <div>
                <p className="text-sm font-600 text-gray-500 mb-2">Nenhuma assinatura encontrada</p>
                <button
                  onClick={() => loadDiagnostic()}
                  className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors mx-auto"
                >
                  <i className="ri-refresh-line" />Recarregar
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
